// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, WrappedComponentProps, injectIntl, FormattedMessage } from "react-intl";
import { Button, Form } from "react-bootstrap";

import { TimePeriod } from "types";
import { IState } from "states/states";
import {
  IAction,
  ActionType,
  raiseAction,
  IActionTypeSetPeriod,
  ActionGraphCommand,
} from "actions";
import { getLocalDate, isSet } from "utils";
import { getGraphBoundaryDates, getPeriodStart, toDropdownItems, isSafariBrowser } from "guiutils";
import { IPeriodData } from "states/guistates";

import Dropdown from "view/comp/Dropdown";

interface IPeriodSelectExtProps {
  identifier: "trendPeriod" | "ioPeriod" | "chargingSessionFilter";
  className?: string;
  periodOptions?: TimePeriod[];
  updateAction?: ActionType;
  updateActionArgs?: any;
  style?: any;
}

interface IPeriodSelectProps {
  timePeriod: TimePeriod;
  startDate: number | undefined;
  endDate: number | undefined;
  isAtStart: boolean;
  isAtEnd: boolean;
  dataStartDate: number | undefined;
}

const mapStateToProps = (state: IState, ownProps: IPeriodSelectExtProps) => {
  const curPeriod = state.gui[ownProps.identifier] as IPeriodData;
  const xZoom = curPeriod.timePeriod;
  // which trend data is used as the bases to determine the start?
  const minTimestamp =
    ownProps.identifier === "chargingSessionFilter"
      ? state.ocpp.minSessionTimestamp
      : state.trending.minTrendTimestamp;
  const dataStart = isSet(minTimestamp)
    ? getPeriodStart(minTimestamp, xZoom, state.site.tzOffset ?? 0)
    : undefined;
  const graphDates = getGraphBoundaryDates(
    curPeriod.startDate,
    curPeriod.endDate,
    state.site.tzOffset,
    xZoom,
  );

  const props: IPeriodSelectProps = {
    timePeriod: xZoom,
    startDate: graphDates.startDate,
    endDate: graphDates.endDate,
    isAtStart: isSet(graphDates.startDate) && isSet(dataStart) && dataStart >= graphDates.startDate,
    isAtEnd: !isSet(curPeriod.startDate),
    dataStartDate: dataStart,
  };
  return props;
};

interface IPeriodSelectDispatch {
  onPeriodChanged: (
    curPeriod: TimePeriod,
    newPeriod: TimePeriod,
    targetStartDate?: number,
    targetEndDate?: number,
  ) => void;
  onSetGraph: (
    command: ActionGraphCommand,
    targetStartDate?: Date | number,
    targetEndDate?: Date | number,
  ) => void;
}

const mapDispatchToProps = (
  dispatch: (action: IAction<any>) => void,
  ownProps: IPeriodSelectExtProps,
) => {
  const dispatchProps: IPeriodSelectDispatch = {
    onPeriodChanged: (
      curPeriod: TimePeriod,
      newPeriod: TimePeriod,
      targetStartDate?: number,
      targetEndDate?: number,
    ) => {
      let periodLengthFallback = 24 * 3600; // min periodLenght - 1 day

      // check current TimePeriod-Setting --
      curPeriod === TimePeriod.WEEK
        ? (periodLengthFallback *= 7)
        : curPeriod === TimePeriod.MONTH
        ? (periodLengthFallback *= 30)
        : (periodLengthFallback *= 365);

      const payload: IActionTypeSetPeriod = {
        identifier: ownProps.identifier,
        command: ActionGraphCommand.SET_PERIOD,
        period: newPeriod,
        timestampStart: targetStartDate,
        timestampEnd:
          newPeriod === TimePeriod.CUSTOM
            ? targetEndDate
              ? targetEndDate
              : targetStartDate
              ? targetStartDate + periodLengthFallback
              : undefined
            : undefined,
      };
      // load new graph data
      dispatch(raiseAction(ActionType.ACT_CHANGE_PERIOD, payload));
      if (ownProps.updateAction) {
        dispatch(raiseAction(ownProps.updateAction, ownProps.updateActionArgs));
      }
    },
    onSetGraph: (
      command: ActionGraphCommand,
      targetStartDate?: Date | number,
      targetEndDate?: Date | number,
    ) => {
      const payload: IActionTypeSetPeriod = {
        identifier: ownProps.identifier,
        command,
        timestampStart: targetStartDate
          ? targetStartDate instanceof Date
            ? Math.round(targetStartDate.getTime() / 1000)
            : targetStartDate
          : undefined,
        timestampEnd: targetEndDate
          ? targetEndDate instanceof Date
            ? Math.round(targetEndDate.getTime() / 1000)
            : targetEndDate
          : undefined,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_PERIOD, payload));
      if (ownProps.updateAction) {
        dispatch(raiseAction(ownProps.updateAction, ownProps.updateActionArgs));
      }
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  selectCaption: {
    id: "Select period",
  },
  selectDate: {
    id: "Select Date",
  },
  startDate: {
    id: "Start Date",
    defaultMessage: "Start",
  },
  endDate: {
    id: "End Date",
    defaultMessage: "End",
  },
});

class PeriodSelectComponent extends React.Component<
  IPeriodSelectProps & IPeriodSelectExtProps & IPeriodSelectDispatch & WrappedComponentProps
> {
  public render() {
    // set default options, if not overwritten by parent component
    const periodOptions = this.props.periodOptions ?? [
      TimePeriod.DAY,
      TimePeriod.WEEK,
      TimePeriod.MONTH,
      TimePeriod.YEAR,
      TimePeriod.CUSTOM,
    ];
    const periodItems = toDropdownItems(periodOptions, "TimePeriod", (_name, value) =>
      this.props.onPeriodChanged(
        this.props.timePeriod,
        value as any as TimePeriod,
        this.props.startDate,
        this.props.endDate,
      ),
    );
    const classes = ["nowrap", "d-print-none"];
    if (this.props.className) {
      classes.push(this.props.className);
    }

    // check browser
    // this query for "isSafariBrowser" is needed, due to different behavior between
    // different browsers on the date-picker element. The element is available on all
    // browsers, but they behave different for our needs - so we can't use Modernizr
    const isDatePickerSupported = !isSafariBrowser();

    return (
      <div id="periodselect" className={classes.join(" ")} style={this.props.style}>
        <Button
          style={{ marginBottom: 2 }}
          id="period-goto-start"
          variant="secondary"
          className="me-1"
          onClick={(event) => this.props.onSetGraph(ActionGraphCommand.START)}
          disabled={
            !isSet(this.props.dataStartDate) ||
            this.props.isAtStart ||
            this.props.timePeriod === TimePeriod.ALL
          }
        >
          |&lt;
        </Button>
        <Button
          style={{ marginBottom: 2 }}
          id="period-go-backward"
          variant="secondary"
          className={"me-1"}
          onClick={(event) => this.props.onSetGraph(ActionGraphCommand.BACKWARD)}
          disabled={
            this.props.isAtStart ||
            this.props.timePeriod === TimePeriod.ALL ||
            this.props.timePeriod === TimePeriod.CUSTOM
          }
        >
          &lt;
        </Button>
        <Dropdown
          className="me-1"
          items={periodItems}
          disabled={this.props.periodOptions?.length === 1 ? true : false}
          caption={this.props.intl.formatMessage(messages.selectCaption)}
          curItem={this.props.intl.formatMessage({
            id: `TimePeriod_${this.props.timePeriod ?? ""}`,
          })}
        />
        <div style={{ display: "inline-block", verticalAlign: "middle" }}>
          <input
            type="date"
            min={getLocalDate(
              this.props.dataStartDate ? this.props.dataStartDate * 1000 : undefined,
            )}
            max={getLocalDate(new Date())}
            id="startDate"
            style={
              isDatePickerSupported
                ? { display: "inline-block", visibility: "hidden", width: 0, padding: 0 }
                : { display: "inline-block", height: "35px", width: "120px" }
            }
            value={getLocalDate(this.props.startDate ? this.props.startDate * 1000 : new Date())}
            onChange={(event) => {
              // check if clear button was tapped/clicked inside the date-control -> this will return null -> ignore it
              if (event.currentTarget.valueAsDate !== null) {
                this.props.onSetGraph(
                  ActionGraphCommand.SET_DATE,
                  event.currentTarget.valueAsDate ?? undefined,
                  this.props.timePeriod === TimePeriod.CUSTOM ? this.props.endDate : undefined,
                );
                if (!isDatePickerSupported) {
                  (document.querySelector("#startDate") as HTMLInputElement).blur();
                }
              }
            }}
          />
        </div>
        <Button
          style={
            isDatePickerSupported
              ? { marginBottom: 2 }
              : { visibility: "hidden", width: 0, padding: 0 }
          }
          id="select-date"
          variant="secondary"
          className="me-1"
          onClick={(event) => {
            (document.querySelector("#startDate") as HTMLInputElement).showPicker();
          }}
          disabled={this.props.timePeriod === TimePeriod.ALL}
        >
          <FormattedMessage
            id={
              this.props.timePeriod === TimePeriod.CUSTOM
                ? messages.startDate.id
                : messages.selectDate.id
            }
          />
        </Button>
        {this.props.timePeriod === TimePeriod.CUSTOM && (
          <>
            <div style={{ display: "inline-block", verticalAlign: "middle" }}>
              <input
                type="date"
                id="endDate"
                min={getLocalDate(this.props.startDate ? this.props.startDate * 1000 : new Date())}
                max={getLocalDate(new Date())}
                style={
                  isDatePickerSupported
                    ? { display: "inline-block", visibility: "hidden", width: 0, padding: 0 }
                    : { display: "inline-block", height: "35px", width: "120px" }
                }
                value={getLocalDate(this.props.endDate ? this.props.endDate * 1000 : new Date())}
                onChange={(event) => {
                  // check if clear button was tapped/clicked inside the date-control -> this will return null -> ignore it
                  if (event.currentTarget.valueAsDate !== null) {
                    this.props.onSetGraph(
                      ActionGraphCommand.SET_DATE,
                      this.props.startDate,
                      event.currentTarget.valueAsDate ?? undefined,
                    );

                    if (!isDatePickerSupported) {
                      (document.querySelector("#endDate") as HTMLInputElement).blur();
                    }
                  }
                }}
              />
            </div>
            <Button
              style={
                isDatePickerSupported
                  ? { marginBottom: 2 }
                  : { visibility: "hidden", width: 0, padding: 0 }
              }
              id="select-date"
              variant="secondary"
              className="me-1"
              onClick={(event) => {
                (document.querySelector("#endDate") as HTMLInputElement).showPicker();
              }}
            >
              <FormattedMessage id={messages.endDate.id} />
            </Button>
          </>
        )}

        <Button
          style={{ marginBottom: 2 }}
          id="period-go-forward"
          variant="secondary"
          className="me-1"
          onClick={(event) => this.props.onSetGraph(ActionGraphCommand.FORWARD)}
          disabled={
            this.props.isAtEnd ||
            this.props.timePeriod === TimePeriod.ALL ||
            this.props.timePeriod === TimePeriod.CUSTOM
          }
        >
          &gt;
        </Button>
        <Button
          style={{ marginBottom: 2 }}
          id="period-goto-end"
          variant="secondary"
          onClick={(event) => this.props.onSetGraph(ActionGraphCommand.END)}
          disabled={this.props.isAtEnd || this.props.timePeriod === TimePeriod.ALL}
        >
          &gt;|
        </Button>
      </div>
    );
  }
}

const PeriodSelect = connect(mapStateToProps, mapDispatchToProps)(PeriodSelectComponent);

export default injectIntl(PeriodSelect);
